import { useAuth } from "context/authContext";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import { deleteNotification } from "services/api";
import { readNotification } from "services/api";
import { useState } from "react"; // ✅ Add this at the top
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import MDButton from "components/MDButton";
import { useTranslation } from "react-i18next";

const NotificationMenu = ({ n, setNotifications, handleNotificationsClose }) => {
  const { t } = useTranslation();
  const [openDialog, setOpenDialog] = useState(false);
  const [loading, setLoading] = useState(false);
  const { chats } = useAuth();
  const navigate = useNavigate();
  const handleDelete = async () => {
    setLoading(true);
    await deleteNotification(n.id);
    setNotifications((prev) => prev.filter((notif) => notif.id !== n.id));
    setOpenDialog(false);
    setLoading(false);
  };

  return (
    <>
      <MenuItem
        onClick={async () => {
          if (n.read) {
            if (n.relatedEntityType) {
              if (n.relatedEntityType.toLowerCase().startsWith("chat/")) {
                const chatId = n.relatedEntityType.split("/")[1];
                const chatObj = chats.find((c) => String(c.chatRoomId) === chatId);
                navigate(`/chat/${chatId}`, { state: { chat: chatObj } });
              } else {
                navigate(`/${n.relatedEntityType.toLowerCase()}`);
              }
            }
            return;
          }

          const res = await readNotification(n.id);
          if (res !== "error") {
            setNotifications((prev) =>
              prev.map((notif) =>
                notif.id === n.id
                  ? { ...notif, read: true, readAt: new Date().toISOString() }
                  : notif
              )
            );

            handleNotificationsClose();

            if (n.relatedEntityType) {
              if (n.relatedEntityType.toLowerCase().startsWith("chat/")) {
                const chatId = n.relatedEntityType.split("/")[1];
                const chatObj = chats.find((c) => String(c.chatRoomId) === chatId);
                navigate(`/chat/${chatId}`, { state: { chat: chatObj } });
              } else if (n.relatedEntityType?.toLowerCase() === "job_application") {
                navigate("/recruiting");
              } else {
                navigate(`/${n.relatedEntityType.toLowerCase()}`);
              }
            }
          }
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          py: 1.5,
          px: 2,
          whiteSpace: "normal",
          maxWidth: 400,
          backgroundColor: n.read ? "inherit" : "rgba(0,0,0,0.04)",
          borderRadius: 1,
        }}
      >
        {/* Notification Content */}
        <Box sx={{ flex: 1, pr: 1 }}>
          <Typography variant="subtitle2" fontWeight={n.read ? "medium" : "bold"} gutterBottom>
            {n.title}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            {n.message}
          </Typography>
          <Typography variant="caption" color="text.disabled">
            {new Date(n.createdAt).toLocaleString()}
          </Typography>
        </Box>

        {/* Delete Button */}
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton
            edge="end"
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              setOpenDialog(true);
            }}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      </MenuItem>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{t("user.deleteNotificationDialogTitle")}</DialogTitle>
        <DialogContent>{t("user.deleteNotificationDialogMessage")}</DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="flex-end" p={1}>
            <CircularProgress size={20} sx={{ color: "black" }} />
          </Box>
        ) : (
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)}>
              {t("admin.strategicInfo.initiatives.dialog.cancel")}
            </Button>
            <Button onClick={handleDelete} color="error" sx={{ color: "red" }}>
              {t("admin.strategicInfo.initiatives.dialog.delete")}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

NotificationMenu.propTypes = {
  n: PropTypes.object.isRequired,

  setNotifications: PropTypes.func.isRequired,
  handleNotificationsClose: PropTypes.func.isRequired,
};

export default NotificationMenu;
